/* eslint-disable vue/component-definition-name-casing */

import './stylus/main.styl';
import '@mdi/font/css/materialdesignicons.css';

import VueDraggableResizable from 'vue-draggable-resizable/src/components/vue-draggable-resizable.vue';
import Vue, { type Component, type CreateElement } from 'vue';
import Vuetify from 'vuetify';
import themes from './theme';
import type { ThemeOptions } from 'vuetify/types/services/theme';
import { Resize, Intersect, ClickOutside } from 'vuetify/lib/directives';
import VueClipboard from 'vue-clipboard2';
import VueObserveVisibility from 'vue-observe-visibility';
import moment from 'moment-timezone';
import type { i18n as I18n } from 'i18next';
import I18NextVue from 'i18next-vue';
import { resizableColumnsDirective, resizeObserverDirective, safeHtmlDirective } from './customDirective';
import { createTooltipDirective } from './tooltipDirective';

/**
 * Legacy global component registrations.
 *
 * Do not add new components here. Prefer explicit imports with PascalCase in
 * each consuming component instead. Explicit imports enable IDE type checking
 * for props and events.
 */

import AvatarPicture from '@mimir/web/pages/layout/components/AvatarPicture.vue';
import ValueField from '@mimir/web/components/mdf/templates/ValueField.vue';
import BaseDialog from '@mimir/web/components/Common/Dialogs/BaseDialog.vue';
import InfoBubble from '@mimir/web/components/Common/InfoBubble.vue';
import CreateButton from '@mimir/web/pages/settings/CreateButton.vue';
import MTextField from '@mimir/web/components/MTextField.vue';
import MTextArea from '@mimir/web/components/MTextArea.vue';
import MTimeInput from '@mimir/web/components/MTimeInput.vue';
import MAutoComplete from '@mimir/web/components/MAutoComplete.vue';
import MSwitch from '@mimir/web/components/MSwitch.vue';
import MBtn from '@mimir/web/components/MBtn.vue';
import MCombobox from '@mimir/web/components/MCombobox.vue';
import CopyButton from '@mimir/web/pages/item/components/CopyButton.vue';
import DesktopDragThumbnail from '@mimir/web/components/DesktopDragThumbnail.vue';
import type { Store } from 'vuex';
import type { State } from './store/state';
import { initializeSentry } from './util/sentryInitializer';
import type { VueRouter } from 'vue-router/types/router';
import { getCachedResolvedTheme } from './util/localStorage';

/*
  The user's real theme preference is only known once their settings load
  from the server (see bootstrap.ts), well after Vuetify must already be
  initialized. Seeding it from the last-known cached theme (instead of
  always defaulting to dark) avoids a dark-background flash for light-theme
  users between the static loading screen and Layout.vue's bootstrap()
  correcting the theme.
*/
function createVuetify2(): InstanceType<typeof Vuetify> {
  return new Vuetify({
    theme: {
      options: {
        customProperties: true
      },
      dark: (getCachedResolvedTheme() ?? 'dark') === 'dark',
      themes: themes as ThemeOptions['themes']
    },
    icons: {
      values: {
        radioOn: 'radio_button_checked',
        radioOff: 'radio_button_unchecked',
        checkboxOn: 'check_box',
        checkboxOff: 'check_box_outline_blank',
        checkboxIndeterminate: 'indeterminate_check_box',
      },
    },
  });
}

export interface AppRootServices {
  i18next: I18n;
  store?: Store<State>;
  router?: VueRouter;
}

export interface AppRootOptions {
  component: Component;
  props: Record<string, any>;
  services: AppRootServices;
}

/**
 * Builds the unmounted root instance of the application: registers the shared
 * Vue plugins, directives and legacy global components, and wires a fresh
 * Vuetify instance together with the given store and router. Used by the
 * application entry point and by browser tests that mount parts of the UI.
 *
 * Vue 2 registers everything on the global `Vue` constructor, and `Vue.use`
 * installs each plugin object only once per page. The first call's `i18next`
 * therefore stays installed for every later root, whichever instance a later
 * call passes. Vuetify, store and router are per root. Vue 3's `createApp`
 * scopes all of it to the returned app, so the Vue 3 factory has no such
 * limit.
 */
export function createAppRoot(options: AppRootOptions): Vue {
  Vue.component('BaseDialog', BaseDialog);
  Vue.component('VueDraggableResizable', VueDraggableResizable);
  Vue.component('ValueField', ValueField);
  Vue.component('InfoBubble', InfoBubble);
  Vue.component('CreateButton', CreateButton);
  Vue.component('CopyButton', CopyButton);
  Vue.component('m-text-field', MTextField);
  Vue.component('m-textarea', MTextArea);
  Vue.component('m-time-input', MTimeInput);
  Vue.component('m-autocomplete', MAutoComplete);
  Vue.component('m-switch', MSwitch);
  Vue.component('m-btn', MBtn);
  Vue.component('m-combobox', MCombobox);
  Vue.component('AvatarPicture', AvatarPicture);
  Vue.component('desktop-drag-thumbnail', DesktopDragThumbnail);

  // TODO: Get rid of this crude injection when we're certain it's not used anywhere.
  Vue.prototype.moment = moment;

  // Taken from VueClipBoard github - this fixes an issue where v-clipboard doesn't work in v-dialog.
  VueClipboard.config.autoSetContainer = true;

  Vue.use(VueClipboard);
  Vue.use(VueObserveVisibility);
  Vue.use(Vuetify, { directives: { Resize, Intersect, ClickOutside }});
  Vue.use(I18NextVue as any, { i18next: options.services.i18next });

  Vue.directive('resizable-columns', resizableColumnsDirective);
  Vue.directive('safe-html', safeHtmlDirective);
  Vue.directive('onresize', resizeObserverDirective);
  const tooltipDirective = createTooltipDirective(window);
  Vue.directive('tooltip', {
    inserted: tooltipDirective.mounted,
    componentUpdated: tooltipDirective.updated,
    unbind: tooltipDirective.beforeUnmount,
  });

  Vue.config.productionTip = false;
  console.log('Initialized Vue 2.');

  if (typeof process !== 'undefined') {
    if (process.env.NODE_ENV === 'development') {
      Vue.config.performance = true;
      console.log('Development mode enabled.');
    }
  }

  // Conditionally initialize Sentry based on feature flag
  // This needs to be done after the store is initialized
  if (options.services.store) {
    options.services.store.watch(
      (state) => state.config?.tenantConfig?.featureFlags?.sentry,
      (sentryEnabled) => {
        if (sentryEnabled) {
          const tenantId = options.services.store?.getters.user?.tenantId || 'unknown';
          initializeSentry({
            vue: Vue,
            tenantId
          });
        }
      },
      { immediate: true } // Check immediately when store is ready
    );
  }

  return new Vue({
    vuetify: createVuetify2(),
    router: options.services.router,
    store: options.services.store,
    render(h: CreateElement) {
      return h(options.component, { props: options.props });
    },
  });
}

export default function initializeVue2(options: AppRootOptions): void {
  createAppRoot(options).$mount('#app');
}
